import type { Checklist } from '@cognite/apm-client';
import type { DateRangeType } from '@cognite/apm-observation';
import { makeToast } from '@cognite/cogs-lab';
import { useMetrics } from '@cognite/metrics';
import { useSelectedRootLocationConfiguration } from '@infield/features/app-config';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { METRICS_NAMESPACES } from '@infield/features/metrics';
import { useFDMServices } from '@infield/providers/fdm-services';
import { QueryKeys } from '@infield/utils/queryKeys';
import { captureException } from '@sentry/browser';
import type { RefetchOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

// We don't want the service to be called every time the component is rendered
// 30 minutes seems like a reasonable time to keep the data fresh, but we can adjust this if needed
const CHECKLISTS_FROM_SCHEDULER_STALE_TIME = 1800000; // 30 minutes

interface Props {
  dateRangeFilter: DateRangeType;
  onlyManualRefetch?: boolean;
}

export const useFetchChecklistsFromSchedulerQuery = ({
  dateRangeFilter,
  onlyManualRefetch = false,
}: Props): {
  data: Checklist[] | undefined;
  isLoading: boolean;
  refetch: (options?: RefetchOptions) => Promise<{
    data: Checklist[] | undefined;
  }>;
} => {
  const { t } = useTranslation(LOCIZE_NAMESPACES.activity);
  const { checklistSchedulerService } = useFDMServices();
  const sliMetrics = useMetrics(METRICS_NAMESPACES.SLI);
  const metrics = useMetrics(METRICS_NAMESPACES.scheduledChecklists);
  const selectedRootLocation = useSelectedRootLocationConfiguration();
  const sliTimerStartTime = useRef<number | undefined>(undefined);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      QueryKeys.CHECKLISTS_FROM_SCHEDULER,
      selectedRootLocation,
      dateRangeFilter,
    ],
    queryFn: async () => {
      sliTimerStartTime.current = Date.now();
      const checklists = await checklistSchedulerService.getChecklists(
        selectedRootLocation,
        dateRangeFilter
      );

      metrics.track('fetchChecklistsFromScheduler', {
        numberOfReturnedItems: checklists?.length,
      });

      return checklists;
    },
    enabled: !onlyManualRefetch,
    staleTime: CHECKLISTS_FROM_SCHEDULER_STALE_TIME,
    onSuccess: () => {
      const sliTimerEndTime = Date.now();
      sliMetrics.track(QueryKeys.CHECKLISTS_FROM_SCHEDULER, {
        sliTimerMilliseconds: sliTimerStartTime.current
          ? sliTimerEndTime - sliTimerStartTime.current
          : undefined,
        status: 'success',
        networkSpeedMbps: navigator.connection?.downlink,
      });
    },
    onError: (error) => {
      const sliTimerEndTime = Date.now();
      sliMetrics.track(QueryKeys.CHECKLISTS_FROM_SCHEDULER, {
        sliTimerMilliseconds: sliTimerStartTime.current
          ? sliTimerEndTime - sliTimerStartTime.current
          : undefined,
        status: 'error',
        networkSpeedMbps: navigator.connection?.downlink,
      });

      captureException(error, {
        level: 'error',
        tags: {
          queryKey: QueryKeys.CHECKLISTS_FROM_SCHEDULER,
        },
      });

      makeToast(
        {
          body: t(
            'FETCH_CHECKLISTS_TOAST_ERROR',
            'An error occurred while fetching the checklists.'
          ),
          type: 'warning',
        },
        { autoClose: 7000 }
      );
    },
  });

  return {
    data,
    refetch,
    isLoading,
  };
};
